import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { FunnelReport } from "@/components/site/FunnelReport";

const field = "border border-line bg-[#121212] px-4 py-3 text-white placeholder:text-zinc-500 outline-none focus:border-cta";

const ReviewRequest = ({ headers }) => {
  const [f, setF] = useState({ name: "", email: "" });
  const send = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/review-request", f, { headers });
      toast.success(`Review request sent to ${f.email}`);
      setF({ name: "", email: "" });
    } catch (ex) { toast.error(ex?.response?.data?.detail || "Could not send"); }
  };
  return (
    <form onSubmit={send} className="flex flex-col gap-3 sm:flex-row" data-testid="admin-review-request-form">
      <input required placeholder="Buyer first name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={field} data-testid="admin-rr-name" />
      <input required type="email" placeholder="Buyer email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={`${field} flex-1`} data-testid="admin-rr-email" />
      <button className="btn-cta !py-3" data-testid="admin-rr-submit">Send request</button>
    </form>
  );
};

export default function Admin() {
  const [key, setKey] = useState(() => sessionStorage.getItem("fm_admin") || "");
  const [data, setData] = useState(null);
  const headers = { "X-Admin-Key": key };

  const load = useCallback(async () => {
    if (!api || !key) return;
    try {
      const [r, s] = await Promise.all([api.get("/admin/reviews", { headers: { "X-Admin-Key": key } }), api.get("/admin/stats", { headers: { "X-Admin-Key": key } })]);
      setData({ reviews: Array.isArray(r.data) ? r.data : [], stats: s.data });
      sessionStorage.setItem("fm_admin", key);
    } catch { toast.error("Wrong admin key"); setData(null); }
  }, [key]);

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots"; meta.content = "noindex";
    document.head.appendChild(meta);
    return () => { meta.remove(); };
  }, []);

  const moderate = async (id, action) => {
    await api.post(`/admin/reviews/${id}/${action}`, {}, { headers });
    load();
  };

  if (!data) {
    return (
      <main className="mx-auto max-w-md px-6 pt-36" data-testid="admin-login">
        <h1 className="font-display text-3xl text-white">Admin</h1>
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="mt-8 flex flex-col gap-3">
          <input type="password" placeholder="Admin key" value={key} onChange={(e) => setKey(e.target.value)} className={field} data-testid="admin-key-input" />
          <button className="btn-cta" data-testid="admin-login-button">Open</button>
        </form>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-6xl px-6 pb-24 pt-32" data-testid="admin-dashboard">
      <h1 className="font-display text-4xl text-white">Admin</h1>
      <div className="mt-8 flex flex-wrap gap-4">
        <div className="border border-line p-5"><p className="eyebrow">Leads</p><p className="font-display text-3xl text-white" data-testid="admin-leads-count">{data.stats?.leads ?? 0}</p></div>
        {(data.stats?.events ?? []).slice(0, 6).map((e) => (
          <div key={e.name} className="border border-line p-5"><p className="eyebrow">{e.name}</p><p className="font-display text-3xl text-white">{e.count}</p></div>
        ))}
      </div>
      <FunnelReport adminKey={key} />
      <h2 className="font-display mt-14 text-2xl text-white">Ask a buyer for a review</h2>
      <div className="mt-4"><ReviewRequest headers={headers} /></div>
      <h2 className="font-display mt-14 text-2xl text-white">Reviews</h2>
      <div className="mt-4 divide-y divide-line border border-line">
        {data.reviews.length === 0 && <p className="p-6 text-zinc-400">No reviews yet.</p>}
        {data.reviews.map((r) => (
          <div key={r.id} className="flex flex-col gap-4 p-6 md:flex-row md:items-start md:justify-between" data-testid={`admin-review-${r.id}`}>
            <div className="max-w-2xl">
              <p className="text-white">{r.name} <span className="text-zinc-400">· {r.email} · {r.product} · {r.rating}/5</span></p>
              <p className="mt-2 text-zinc-300">{r.text}</p>
              <p className="eyebrow mt-3">{r.status}</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => moderate(r.id, "approve")} className="btn-cta !py-2 !px-4 text-sm" data-testid={`admin-approve-${r.id}`}>Approve</button>
              <button onClick={() => moderate(r.id, "reject")} className="btn-ghost !py-2 !px-4 text-sm" data-testid={`admin-reject-${r.id}`}>Reject</button>
            </div>
          </div>
        ))}
      </div>
    </main>
  );
}
